import { sessionjson, StitcherAuthMethod } from '@paramount-streaming/blackbird';
import { ITagAbstractClassArgument } from 'tag/ts/tag/interfaces/arguments/ITagAbstractClassArgument';
import { IAviaPlugin } from 'tag/ts/tag/player/adapters/avia/interfaces/IAviaPlugin';
import { AviaPlugin } from 'tag/ts/tag/player/adapters/avia/plugins/AviaPlugin';
import { TPluginConfigFactory } from 'tag/ts/tag/player/adapters/avia/types/TPluginConfigFactory';

/**
 * @copyright © Paramount Streaming
 * @description Wraps sessionjson plugin from pluto-tv to be integrated with SmartTag
 */
export class SessionJsonPlugin extends AviaPlugin implements IAviaPlugin {
	package = sessionjson as unknown as TPluginConfigFactory;
	namespaceKey = 'plugin_sessionjson';

	constructor( argument: ITagAbstractClassArgument ) {
		super(
			{
				...argument,
				name: 'SessionJsonPlugin'
			}
		);
	}

	/**
	 * Should be used if params.STITCHER is non-empty
	 * @returns {boolean}
	 */
	shouldBeUsed() {
		return this.hasStitcherParams();
	}

	/**
	 * Returns parameters required to initialize sessionjson from pluto-tv
	 * @see {@link https://github.com/paramount-streaming/playback-javascript-lib/blob/main/libs/blackbird/src/player/adapters/avia/plugins/sessionjson/README.md}
	 * @example [Initializing sessionjson in Pluto tv demo] https://github.com/paramount-streaming/playback-javascript-lib/blob/main/app/src/smart-tag/useSmartTagVideoPlayer.ts#L86
	 */
	getFactoryParams() {
		const stitcherServers = this.tag.params?.STITCHER?.servers;
		// Converged playout-gateway uses one host for HLS+DASH; fall back to servers.stitcher
		// when servers.stitcherDash is absent so DASH still routes correctly.
		const dashServer = stitcherServers?.stitcherDash ?? stitcherServers?.stitcher;
		return {
			...super.getFactoryParams(),
			// set necessary values from STITCHER params
			minimumUpdateThreshold: this.tag.params?.STITCHER?.minimumUpdateThreshold,
			stitcherDashServer: dashServer,
			stitcherServer: stitcherServers?.stitcher,
			stitcherVersion: this.tag.params?.STITCHER?.stitcherVersion,
			getToken: () => {
				// PLBCK-7813: read window.jwtToken live so Blackbird always gets the current JWT.
				// params.STITCHER.token is a snapshot frozen at load() time; after ~30–40 min the
				// JWT expires and every session.json call 401s. The ?? fallback preserves behavior
				// on P+/CBS.com paths where window.jwtToken is undefined.
				return ( window as Window & { jwtToken?: string } ).jwtToken
					?? this.tag.params?.STITCHER?.token;
			},
			// PLBCK-7541: Send Stitcher tokens via the `x-api-access-token`
			// header instead of the `at` query-string parameter — fixes
			// `session.json` HTTP 414 URI Too Long failures once the token(s)
			// push the URL past the API Router limit. Supersedes the
			// PLBCK-6706 query-string workaround for the earlier CTV issue.
			authMethod: StitcherAuthMethod.HEADER,
			// PLBCK-6706: Provide the API Router `at` token so session.json requests
			// routed through `/apps-api` can be authorized alongside the session JWT.
			getAccessToken: () => {
				return this.tag.params?.STITCHER?.accessToken ?? null;
			}
		};
	}
}